import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Ship, ShieldCheck, Globe2, Package, BadgeIndianRupee, Clock,
  Phone, Mail, MapPin, ArrowRight, Leaf, Sparkles, Flame, Menu, X, Send, Award, ChevronLeft, ChevronRight,
} from "lucide-react";
import { Toaster } from "../components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP = "https://wa.me/919011025500";
const LOGO = "/brand-logo.png";

const MEDIA = {
  hero: "https://images.pexels.com/photos/262353/pexels-photo-262353.jpeg",
  spices: "https://images.pexels.com/photos/5741508/pexels-photo-5741508.jpeg",
  soaps: "https://images.pexels.com/photos/6930879/pexels-photo-6930879.jpeg",
  statues:
    "https://images.unsplash.com/photo-1763475944771-702683b1b42c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBicmFzcyUyMHN0YXR1ZXxlbnwwfHx8fDE3ODI1MDY1ODN8MA&ixlib=rb-4.1.0&q=85",
};

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

const NAV = [
  { label: "Products", href: "#products" },
  { label: "Kunsh Masale", href: "#kunsh" },
  { label: "Fiber Idols", href: "#fiber" },
  { label: "Capabilities", href: "#capabilities" },
  { label: "Contact", href: "#contact" },
];

const FIBER_PAGES = Array.from({ length: 46 }, (_, i) => `/fiber/${String(i + 2).padStart(2, "0")}.jpg`);

const WOODEN_IDOLS = [
  { img: "/fiber-wooden/w5.jpg", name: "Pagadi Ganesha" },
  { img: "/fiber-wooden/w1.jpg", name: "Mukut Ganesha" },
  { img: "/fiber-wooden/w4.jpg", name: "Leaf Ganesha" },
  { img: "/fiber-wooden/w3.jpg", name: "Sherawali Mata (Durga)" },
  { img: "/fiber-wooden/w2.jpg", name: "Shree Swami Samarth" },
];

const KUNSH_COVER = "/kunsh/01.jpg";
const KUNSH_PAGES = [
  { img: "/kunsh/03.jpg", title: "Chilli Powders", note: "Laal Pari, Tarri, Kashmiri & Super" },
  { img: "/kunsh/04.jpg", title: "Turmeric & Coriander", note: "Premium & Super grades" },
  { img: "/kunsh/05.jpg", title: "Kala, Sunday, Goda & Garam", note: "Signature masala blends" },
  { img: "/kunsh/06.jpg", title: "Kanda Lasun & Chatpat", note: "Everyday favourites" },
  { img: "/kunsh/07.jpg", title: "Non-Veg Masala", note: "Mutton, Chicken, Biryani, Egg, Fish" },
  { img: "/kunsh/08.jpg", title: "Kitchen King & More", note: "Sabji, Pav Bhaji, Sambhar, Paneer, Chhole" },
  { img: "/kunsh/09.jpg", title: "Chaat & Regional", note: "Pani Puri, Misal, Jaljeera, Chaat" },
  { img: "/kunsh/10.jpg", title: "Gravy Mix Spices", note: "Sabji & Mutton gravy bases" },
  { img: "/kunsh/11.jpg", title: "Seasonal Special", note: "Papad & Chivda masala" },
  { img: "/kunsh/12.jpg", title: "Zakas Zhatka — Asli Desi", note: "Chilli, Turmeric, Coriander, Kanda Lasun" },
  { img: "/kunsh/13.jpg", title: "Zakas Zhatka — Blends", note: "Chatpat & Kala Tikhat" },
];

const CATEGORIES = [
  {
    key: "spices",
    name: "Kunsh Masale",
    tagline: "भरोसा स्वाद भरा — The Real Taste of Tradition",
    desc: "Our own ISO 9001 certified spice brand: chilli, turmeric & coriander powders plus 40+ authentic masala blends — from Garam & Kanda Lasun to Biryani, Chaat & regional specials.",
    img: MEDIA.spices,
    icon: Flame,
    span: "lg:col-span-8 lg:row-span-2",
    height: "min-h-[520px]",
  },
  {
    key: "soaps",
    name: "Handmade Soaps",
    tagline: "Nature's Care in Every Bar",
    desc: "Natural, pure and nourishing bars, handcrafted for healthy skin.",
    img: MEDIA.soaps,
    icon: Leaf,
    span: "lg:col-span-4",
    height: "min-h-[250px]",
  },
  {
    key: "statues",
    name: "Fiber & Resin Idols",
    tagline: "Handcrafted Divinity & Decor",
    desc: "Premium handcrafted deity idols — exported worldwide & supplied across India. 300+ designs.",
    img: "/fiber-wooden/trio.jpg",
    pos: "center 32%",
    icon: Sparkles,
    span: "lg:col-span-4",
    height: "min-h-[250px]",
  },
];

const SPICES = [
  "Chilli Powder", "Turmeric Powder", "Coriander Powder", "Garam Masala",
  "Kanda Lasun Masala", "Chatpat Masala", "Kala Masala", "Chicken Masala",
  "Mutton Masala", "Biryani Masala", "Kitchen King", "Chaat Masala",
];

const CAPABILITIES = [
  { icon: Package, title: "Custom Packaging", desc: "Tailored, brand-ready packaging for every shipment." },
  { icon: Globe2, title: "Worldwide Shipping", desc: "Reliable global logistics to your destination port." },
  { icon: ShieldCheck, title: "Quality Assured", desc: "Rigorous checks ensure premium, consistent products." },
  { icon: Ship, title: "Strong & Safe Packaging", desc: "Damage-free delivery across long distances." },
  { icon: BadgeIndianRupee, title: "Competitive Prices", desc: "Direct sourcing keeps pricing sharp and fair." },
  { icon: Clock, title: "Timely Delivery", desc: "On-time despatch with proactive order support." },
];

const VALUES = ["Quality", "Trust", "Transparency", "Customer Satisfaction"];

// ---------------- Header ----------------
const Header = () => {
  const [open, setOpen] = useState(false);
  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#06291C]/70 border-b border-[#D4AF37]/20"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <a href="#top" data-testid="logo" className="flex items-center gap-3">
          <span className="rounded-xl bg-white p-1.5 flex items-center justify-center">
            <img src={LOGO} alt="Arambh Exim Global" className="h-9 w-9 object-contain" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-serif text-xl sm:text-2xl text-white tracking-tight">
              ARAMBH
            </span>
            <span className="text-[10px] tracking-[0.35em] text-[#9FB8AC] uppercase">Export Import Global</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className="text-sm text-[#9FB8AC] hover:text-[#D4AF37] transition-colors duration-200"
            >
              {n.label}
            </a>
          ))}
          <a
            href="#contact"
            data-testid="header-cta"
            className="rounded-full bg-[#D4AF37] px-6 py-2.5 text-sm font-medium text-[#06291C] hover:bg-[#F3E5AB] transition-colors duration-200"
          >
            Get a Quote
          </a>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-white p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden border-t border-[#D4AF37]/20 bg-[#06291C] px-5 py-4 flex flex-col gap-4">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              onClick={() => setOpen(false)}
              className="text-[#9FB8AC] hover:text-[#D4AF37] transition-colors"
            >
              {n.label}
            </a>
          ))}
          <a href="#contact" onClick={() => setOpen(false)} className="rounded-full bg-[#D4AF37] px-6 py-2.5 text-center text-sm font-medium text-[#06291C]">
            Get a Quote
          </a>
        </div>
      )}
    </header>
  );
};

// ---------------- Hero ----------------
const Hero = () => (
  <section id="top" className="relative min-h-screen flex items-center overflow-hidden">
    <div
      className="absolute inset-0 bg-cover bg-center"
      style={{ backgroundImage: `url(${MEDIA.hero})` }}
    />
    <div className="absolute inset-0 bg-[#06291C]/80" />
    <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 30% 40%, rgba(212,175,55,0.12), transparent 55%)" }} />

    <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-28 pb-16 w-full">
      <motion.p
        variants={fadeUp} initial="hidden" animate="show" custom={0}
        className="text-[#D4AF37] tracking-[0.35em] text-xs sm:text-sm uppercase mb-6"
      >
        Pune, India · Exporting Worldwide
      </motion.p>
      <motion.h1
        variants={fadeUp} initial="hidden" animate="show" custom={1}
        className="font-serif text-4xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-4xl tracking-tight"
      >
        Delivering Quality. Building Trust.{" "}
        <span className="text-[#D4AF37]">Creating Global Opportunities.</span>
      </motion.h1>
      <motion.p
        variants={fadeUp} initial="hidden" animate="show" custom={2}
        className="mt-7 max-w-xl text-base sm:text-lg text-[#9FB8AC]"
      >
        Premium Indian spices, handmade soaps and handcrafted fiber & resin statues —
        sourced with care and shipped to markets across the globe.
      </motion.p>
      <motion.div
        variants={fadeUp} initial="hidden" animate="show" custom={3}
        className="mt-10 flex flex-wrap gap-4"
      >
        <a
          href="#contact"
          data-testid="hero-quote-btn"
          className="group inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-8 py-3.5 font-medium text-[#06291C] hover:bg-[#F3E5AB] transition-colors duration-200 gold-glow"
        >
          Get a Quote
          <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-200" />
        </a>
        <a
          href="#products"
          data-testid="hero-products-btn"
          className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 px-8 py-3.5 font-medium text-white hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-200"
        >
          Explore Products
        </a>
      </motion.div>

      <motion.div
        variants={fadeUp} initial="hidden" animate="show" custom={4}
        className="mt-16 grid grid-cols-3 max-w-xl gap-6 border-t border-[#D4AF37]/20 pt-8"
      >
        {[
          { n: "3", l: "Product Verticals" },
          { n: "Global", l: "Shipping Reach" },
          { n: "100%", l: "Quality Assured" },
        ].map((s) => (
          <div key={s.l} data-testid={`hero-stat-${s.l}`}>
            <div className="font-serif text-2xl sm:text-3xl text-[#D4AF37]">{s.n}</div>
            <div className="text-xs sm:text-sm text-[#9FB8AC] mt-1">{s.l}</div>
          </div>
        ))}
      </motion.div>
    </div>
  </section>
);

// ---------------- Products ----------------
const Products = () => (
  <section id="products" className="relative py-24 px-5 sm:px-8">
    <div className="max-w-7xl mx-auto">
      <SectionHead eyebrow="Our Offerings" title="Crafted in India, Loved Worldwide" />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-14">
        {CATEGORIES.map((c, i) => (
          <motion.div
            key={c.key}
            variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
            data-testid={`product-card-${c.key}`}
            className={`group relative overflow-hidden rounded-2xl border border-[#D4AF37]/20 ${c.span} ${c.height}`}
          >
            <div
              className="absolute inset-0 bg-cover transition-transform duration-700 group-hover:scale-110"
              style={{ backgroundImage: `url(${c.img})`, backgroundPosition: c.pos || "center" }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#06291C] via-[#06291C]/50 to-transparent" />
            <div className="relative z-10 h-full flex flex-col justify-end p-7">
              <c.icon className="text-[#D4AF37] mb-3" size={26} />
              <h3 className="font-serif text-2xl text-white">{c.name}</h3>
              <p className="text-[#D4AF37] text-sm mt-1">{c.tagline}</p>
              <p className="text-[#9FB8AC] text-sm mt-3 max-w-md">{c.desc}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Spice line-up */}
      <div className="mt-12 rounded-2xl border border-[#D4AF37]/20 bg-[#0C3A29] p-8">
        <h4 className="font-serif text-xl text-white mb-5">Kunsh Signature Range</h4>
        <div className="flex flex-wrap gap-3">
          {SPICES.map((s) => (
            <span
              key={s}
              data-testid={`spice-${s}`}
              className="rounded-full border border-[#D4AF37]/30 px-4 py-2 text-sm text-[#F3E5AB] hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors duration-200"
            >
              {s}
            </span>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// ---------------- Kunsh Masale ----------------
const KunshMasale = () => {
  const [active, setActive] = useState(null); // index into KUNSH_PAGES

  const close = () => setActive(null);
  const prev = () => setActive((i) => (i - 1 + KUNSH_PAGES.length) % KUNSH_PAGES.length);
  const next = () => setActive((i) => (i + 1) % KUNSH_PAGES.length);

  return (
    <section id="kunsh" className="relative py-24 px-5 sm:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Brand intro */}
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="lg:col-span-5"
          >
            <p className="text-[#E86A56] tracking-[0.3em] text-xs uppercase mb-4">Our Spice Brand</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">
              Kunsh Masale
            </h2>
            <p className="text-[#D4AF37] font-serif text-lg mt-3">भरोसा स्वाद भरा · The Real Taste of Tradition</p>
            <p className="text-[#9FB8AC] mt-5 max-w-md">
              Kunsh is our flagship, ISO 9001 certified masala brand — authentic Indian
              spices and 40+ ready blends crafted for rich colour, aroma and taste.
              From everyday chilli & turmeric powders to Biryani, Kanda Lasun, Chaat
              and regional specials, available across retail and export pack sizes.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <span className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 px-4 py-2 text-sm text-[#F3E5AB]">
                <Award size={16} className="text-[#D4AF37]" /> ISO 9001 Certified
              </span>
              <a href="#contact" data-testid="kunsh-enquire-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[#D6402E] px-6 py-2.5 text-sm font-medium text-white hover:bg-[#e0503e] transition-colors duration-200">
                Enquire for Bulk / Export <ArrowRight size={16} />
              </a>
            </div>
          </motion.div>

          <motion.div
            variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1}
            className="lg:col-span-7"
          >
            <div className="relative rounded-2xl overflow-hidden border border-[#D4AF37]/30 gold-glow">
              <img src={KUNSH_COVER} alt="Kunsh Masale brand" className="w-full h-auto object-contain bg-black" />
            </div>
          </motion.div>
        </div>

        {/* Catalog grid */}
        <h3 className="font-serif text-2xl text-white mt-16 mb-8">Product Catalogue</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {KUNSH_PAGES.map((p, i) => (
            <motion.button
              type="button"
              key={p.img}
              onClick={() => setActive(i)}
              data-testid={`kunsh-page-${i}`}
              variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i % 4}
              className="group text-left rounded-xl overflow-hidden border border-[#D4AF37]/20 bg-black hover:border-[#D4AF37]/70 transition-colors duration-200"
            >
              <div className="relative overflow-hidden">
                <img src={p.img} alt={p.title} loading="lazy"
                  className="w-full h-56 object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                <span className="absolute top-3 right-3 rounded-full bg-[#D6402E] px-2.5 py-1 text-[10px] font-medium text-white uppercase tracking-wide">View</span>
              </div>
              <div className="p-4">
                <h4 className="font-serif text-base text-white leading-tight">{p.title}</h4>
                <p className="text-[#9FB8AC] text-xs mt-1">{p.note}</p>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {active !== null && (
        <div
          data-testid="kunsh-lightbox"
          className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={close}
        >
          <button data-testid="kunsh-lightbox-close" onClick={close}
            className="absolute top-5 right-5 text-white/80 hover:text-[#D4AF37] transition-colors" aria-label="Close">
            <X size={30} />
          </button>
          <button data-testid="kunsh-lightbox-prev" onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-3 sm:left-8 text-white/80 hover:text-[#D4AF37] transition-colors" aria-label="Previous">
            <ChevronLeft size={40} />
          </button>
          <img
            src={KUNSH_PAGES[active].img}
            alt={KUNSH_PAGES[active].title}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[88vh] max-w-full sm:max-w-3xl rounded-lg border border-[#D4AF37]/30 object-contain"
          />
          <button data-testid="kunsh-lightbox-next" onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-3 sm:right-8 text-white/80 hover:text-[#D4AF37] transition-colors" aria-label="Next">
            <ChevronRight size={40} />
          </button>
        </div>
      )}
    </section>
  );
};


// ---------------- Fiber Idols ----------------
const FiberIdols = () => {
  const [active, setActive] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const close = () => setActive(null);
  const prev = () => setActive((i) => (i - 1 + FIBER_PAGES.length) % FIBER_PAGES.length);
  const next = () => setActive((i) => (i + 1) % FIBER_PAGES.length);

  const visible = showAll ? FIBER_PAGES : FIBER_PAGES.slice(0, 12);

  return (
    <section id="fiber" className="relative py-24 px-5 sm:px-8 bg-[#082e20]">
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        <SectionHead eyebrow="Handcrafted Idols" title="Fiber & Resin Idols" />
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1}
          className="mt-5 max-w-2xl mx-auto text-center"
        >
          <p className="text-[#D4AF37] font-serif text-lg">Crafting Tradition, Delivering Worldwide</p>
          <p className="text-[#9FB8AC] mt-4">
            A vast catalogue of premium handcrafted deity idols and decorative artifacts —
            Ganesha, Radha-Krishna, Lakshmi, Ram Darbar, Shiva and many more. Durably
            elegant, richly detailed and available in multiple sizes, for global export and domestic supply.
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            {[
              { icon: Globe2, t: "Global Export" },
              { icon: Ship, t: "Domestic Supply" },
              { icon: Sparkles, t: "Customization" },
              { icon: ShieldCheck, t: "Premium Quality" },
            ].map((b) => (
              <div key={b.t} className="flex items-center gap-2 rounded-full border border-[#D4AF37]/20 bg-[#0C3A29] px-4 py-2">
                <b.icon size={15} className="text-[#D4AF37]" />
                <span className="text-white text-sm">{b.t}</span>
              </div>
            ))}
          </div>
          <a href="#contact" data-testid="fiber-enquire-btn"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#D6402E] px-6 py-2.5 text-sm font-medium text-white hover:bg-[#e0503e] transition-colors duration-200">
            Enquire for Export / Domestic <ArrowRight size={16} />
          </a>
        </motion.div>

        {/* Wooden finish collection */}
        <h3 className="font-serif text-2xl text-white mt-16 mb-2">Wooden Finish Collection</h3>
        <p className="text-[#9FB8AC] mb-8 max-w-xl">Premium fiber idols crafted with a rich, hand-finished wooden look.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
          {WOODEN_IDOLS.map((it, i) => (
            <motion.div
              key={it.img}
              variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              data-testid={`wooden-idol-${i}`}
              className="group rounded-xl overflow-hidden border border-[#D4AF37]/20 bg-[#0C3A29] hover:border-[#D4AF37]/70 transition-colors duration-200"
            >
              <div className="overflow-hidden bg-[#06291C]">
                <img src={it.img} alt={it.name} loading="lazy"
                  className="w-full h-60 object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-3 text-center">
                <h4 className="font-serif text-base text-white leading-tight">{it.name}</h4>
                <span className="text-[10px] tracking-[0.2em] text-[#D4AF37] uppercase">Wooden Finish</span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Catalogue grid */}
        <h3 className="font-serif text-2xl text-white mt-16 mb-8">Idol Catalogue</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {visible.map((src, i) => (
            <motion.button
              type="button"
              key={src}
              onClick={() => setActive(i)}
              data-testid={`fiber-page-${i}`}
              variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i % 4}
              className="group relative rounded-xl overflow-hidden border border-[#D4AF37]/20 bg-black hover:border-[#D4AF37]/70 transition-colors duration-200"
            >
              <img src={src} alt={`Fiber idols page ${i + 2}`} loading="lazy"
                className="w-full h-52 object-cover transition-transform duration-500 group-hover:scale-105" />
              <span className="absolute top-3 right-3 rounded-full bg-[#D6402E] px-2.5 py-1 text-[10px] font-medium text-white uppercase tracking-wide">View</span>
            </motion.button>
          ))}
        </div>

        {!showAll && (
          <div className="mt-10 text-center">
            <button
              type="button"
              data-testid="fiber-view-all"
              onClick={() => setShowAll(true)}
              className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 px-8 py-3 text-white hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-200"
            >
              View All {FIBER_PAGES.length} Pages <ArrowRight size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {active !== null && (
        <div
          data-testid="fiber-lightbox"
          className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={close}
        >
          <button data-testid="fiber-lightbox-close" onClick={close}
            className="absolute top-5 right-5 text-white/80 hover:text-[#D4AF37] transition-colors" aria-label="Close">
            <X size={30} />
          </button>
          <button data-testid="fiber-lightbox-prev" onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-3 sm:left-8 text-white/80 hover:text-[#D4AF37] transition-colors" aria-label="Previous">
            <ChevronLeft size={40} />
          </button>
          <img
            src={FIBER_PAGES[active]}
            alt={`Fiber idols page ${active + 2}`}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[88vh] max-w-full sm:max-w-4xl rounded-lg border border-[#D4AF37]/30 object-contain"
          />
          <button data-testid="fiber-lightbox-next" onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-3 sm:right-8 text-white/80 hover:text-[#D4AF37] transition-colors" aria-label="Next">
            <ChevronRight size={40} />
          </button>
        </div>
      )}
    </section>
  );
};

// ---------------- Capabilities ----------------
const Capabilities = () => (
  <section id="capabilities" className="relative py-24 px-5 sm:px-8 bg-[#082e20]">
    <div className="max-w-7xl mx-auto">
      <SectionHead eyebrow="Export & Supply" title="Why Partners Choose Arambh" />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
        {CAPABILITIES.map((c, i) => (
          <motion.div
            key={c.title}
            variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
            data-testid={`capability-${i}`}
            className="rounded-2xl border border-[#D4AF37]/20 bg-[#0C3A29] p-7 hover:border-[#D4AF37]/60 transition-colors duration-200"
          >
            <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-5">
              <c.icon className="text-[#D4AF37]" size={24} />
            </div>
            <h3 className="font-serif text-xl text-white">{c.title}</h3>
            <p className="text-[#9FB8AC] text-sm mt-2">{c.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// ---------------- About ----------------
const About = () => (
  <section id="about" className="relative py-24 px-5 sm:px-8">
    <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
      <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
        <p className="text-[#E86A56] tracking-[0.3em] text-xs uppercase mb-4">About Us</p>
        <h2 className="font-serif text-3xl sm:text-4xl leading-tight tracking-tight">
          A trusted bridge between Indian craftsmanship and global markets.
        </h2>
        <p className="mt-6 text-[#9FB8AC]">
          Arambh Exim Global is a Pune-based export & supply company committed to
          delivering premium Indian products worldwide. Our mission is to deliver
          quality products, build long-term relationships and create global
          opportunities for our partners.
        </p>
        <div className="mt-8 grid grid-cols-2 gap-4">
          {VALUES.map((v) => (
            <div
              key={v}
              data-testid={`value-${v}`}
              className="rounded-xl border border-[#D4AF37]/20 bg-[#0C3A29] px-5 py-4 flex items-center gap-3"
            >
              <span className="w-2 h-2 rounded-full bg-[#D4AF37]" />
              <span className="text-white text-sm">{v}</span>
            </div>
          ))}
        </div>
      </motion.div>

      <motion.div
        variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1}
        className="relative rounded-2xl overflow-hidden border border-[#D4AF37]/20 min-h-[420px]"
      >
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${MEDIA.spices})` }} />
        <div className="absolute inset-0 bg-[#06291C]/40" />
      </motion.div>
    </div>
  </section>
);

// ---------------- Contact ----------------
const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", product_interest: "General", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Thank you! Your inquiry has been received.");
      setForm({ name: "", email: "", phone: "", product_interest: "General", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or WhatsApp us.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-lg bg-[#06291C] border border-[#D4AF37]/30 px-4 py-3 text-white placeholder-[#5c7a6b] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-colors duration-200";

  return (
    <section id="contact" className="relative py-24 px-5 sm:px-8 bg-[#082e20]">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14">
        <div>
          <SectionHead eyebrow="Contact" title="Let's Build Global Trade Together" align="left" />
          <p className="text-[#9FB8AC] mt-6 max-w-md">
            Share your requirement and our team will get back with pricing,
            packaging options and shipping details.
          </p>

          <div className="mt-10 space-y-5">
            <a href="tel:+919011025500" data-testid="contact-phone" className="flex items-center gap-4 group">
              <span className="w-11 h-11 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center"><Phone className="text-[#D4AF37]" size={20} /></span>
              <span className="text-white group-hover:text-[#D4AF37] transition-colors">+91 90110 25500</span>
            </a>
            <a href="tel:+917350521115" data-testid="contact-phone-2" className="flex items-center gap-4 group">
              <span className="w-11 h-11 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center"><Phone className="text-[#D4AF37]" size={20} /></span>
              <span className="text-white group-hover:text-[#D4AF37] transition-colors">+91 73505 21115</span>
            </a>
            <a href="mailto:arambh5252@gmail.com" data-testid="contact-email" className="flex items-center gap-4 group">
              <span className="w-11 h-11 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center"><Mail className="text-[#D4AF37]" size={20} /></span>
              <span className="text-white group-hover:text-[#D4AF37] transition-colors">arambh5252@gmail.com</span>
            </a>
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center"><MapPin className="text-[#D4AF37]" size={20} /></span>
              <span className="text-white">Pune, Maharashtra, India</span>
            </div>
            <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp"
              className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 px-6 py-3 text-white hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-200">
              Chat on WhatsApp <ArrowRight size={16} />
            </a>
          </div>
        </div>

        <motion.form
          variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
          onSubmit={submit}
          data-testid="inquiry-form"
          className="rounded-2xl border border-[#D4AF37]/20 bg-[#0C3A29] p-7 sm:p-9 gold-glow"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="input-name" className={inputCls} placeholder="Full Name *" value={form.name} onChange={update("name")} />
            <input data-testid="input-email" type="email" className={inputCls} placeholder="Email *" value={form.email} onChange={update("email")} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <input data-testid="input-phone" className={inputCls} placeholder="Phone" value={form.phone} onChange={update("phone")} />
            <select data-testid="input-product" className={inputCls} value={form.product_interest} onChange={update("product_interest")}>
              <option>General</option>
              <option>Premium Indian Spices</option>
              <option>Handmade Soaps</option>
              <option>Fiber & Resin Statues</option>
            </select>
          </div>
          <textarea
            data-testid="input-message"
            className={`${inputCls} mt-4 resize-none`}
            rows={5}
            placeholder="Tell us about your requirement *"
            value={form.message}
            onChange={update("message")}
          />
          <button
            type="submit"
            data-testid="submit-inquiry-btn"
            disabled={loading}
            className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-8 py-3.5 font-medium text-[#06291C] hover:bg-[#F3E5AB] transition-colors duration-200 disabled:opacity-60"
          >
            {loading ? "Sending..." : "Send Inquiry"}
            {!loading && <Send size={17} />}
          </button>
        </motion.form>
      </div>
    </section>
  );
};

// ---------------- Footer ----------------
const Footer = () => (
  <footer data-testid="site-footer" className="border-t border-[#D4AF37]/20 py-12 px-5 sm:px-8">
    <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
      <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
        <span className="rounded-xl bg-white p-2 flex items-center justify-center">
          <img src={LOGO} alt="Arambh Exim Global" className="h-14 w-14 object-contain" />
        </span>
        <div>
          <div className="font-serif text-xl">ARAMBH <span className="text-[#D4AF37]">GLOBAL</span></div>
          <p className="text-[#9FB8AC] text-sm mt-1">Delivering Quality. Building Trust. Creating Global Opportunities.</p>
        </div>
      </div>
      <div className="text-[#9FB8AC] text-sm text-center sm:text-right">
        <div>www.arambh5252.com</div>
        <div className="mt-1">© {new Date().getFullYear()} Arambh Exim Global. All rights reserved.</div>
      </div>
    </div>
  </footer>
);

// ---------------- Shared ----------------
const SectionHead = ({ eyebrow, title, align = "center" }) => (
  <motion.div
    variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
    className={align === "center" ? "text-center max-w-2xl mx-auto" : "max-w-2xl"}
  >
    <p className="text-[#E86A56] tracking-[0.3em] text-xs uppercase mb-4">{eyebrow}</p>
    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">{title}</h2>
  </motion.div>
);

export default function Landing() {
  return (
    <div className="grain relative">
      <Toaster position="top-center" richColors />
      <Header />
      <main>
        <Hero />
        <Products />
        <KunshMasale />
        <FiberIdols />
        <Capabilities />
        <About />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}
